import React, { useEffect, useRef, useState } from "react";
import {
  User,
  IndianRupee,
  Layers3,
  ChevronDown,
  ChevronUp,
  List,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getCourseNestedDetail,
  getChapters,
  updateCourseApprovalStatus,
  resetCourseApprovalStatus,
} from "../../State/Course/Action";
import { Box, CircularProgress } from "@mui/material";
import AdminSidebar from "../Admin/AdminSideBar";
import { getChaptersByModuleId } from "../../State/Chapter/Action";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  DialogActions,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";
import { PlayCircle, X } from "lucide-react";
import {
  resetModuleApprovalStatus,
  updateModuleApprovalStatus,
} from "../../State/Module/Action";

const CourseNestedDetail = () => {
  const { courseId } = useParams();
  const dispatch = useDispatch();
  const [openModules, setOpenModules] = useState({});
  const chaptersByModule = useSelector(
    (store) => store.chapter.chapterListResponse || {}
  );
  const chaptersLoading = useSelector((store) => store.chapter.loading || {});

  const approvalStatusChangeResponse = useSelector(
    (store) => store.course.approvalStatusChangeResponse
  );

  const moduleApprovalStatusChangeResponse = useSelector(
    (store) => store.module.moduleApprovalStatusChangeResponse
  );

  const course = useSelector((store) => store.course.courseNestedDetail);
  const loading = useSelector((store) => store.course.loading);

  // Inside component
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");

  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusDialogMessage, setStatusDialogMessage] = useState("");
  const [statusType, setStatusType] = useState(null);
  const [item, setItem] = useState("Course");

  const firstCourseRender = useRef(true);
  const firstModuleRender = useRef(true);

  const [clickedStatus, setClickedStatus] = useState({
    course: null, // "APPROVE" or "REJECT"
    module: {}, // moduleId -> "APPROVE" or "REJECT"
  });

  const handlePlayClick = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setOpenVideoDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenVideoDialog(false);
    setSelectedVideoUrl("");
  };

  const handleApprove = (type, id) => {
    if (type === "course") {
      setClickedStatus({ ...clickedStatus, course: "APPROVE" });
      dispatch(updateCourseApprovalStatus(courseId, "APPROVED"));
    } else {
      setClickedStatus((prev) => ({
        ...prev,
        module: { ...prev.module, [id]: "APPROVE" },
      }));
      dispatch(updateModuleApprovalStatus(id, "APPROVED"));
    }

    dispatch(resetModuleApprovalStatus());
    dispatch(resetCourseApprovalStatus());
  };

  const handleReject = (type, id) => {
    if (type === "course") {
      setClickedStatus({ ...clickedStatus, course: "REJECT" });
      dispatch(updateCourseApprovalStatus(courseId, "REJECTED"));
    } else {
      setClickedStatus((prev) => ({
        ...prev,
        module: { ...prev.module, [id]: "REJECT" },
      }));
      dispatch(updateModuleApprovalStatus(id, "REJECTED"));
    }

    dispatch(resetModuleApprovalStatus());
    dispatch(resetCourseApprovalStatus());
  };

  useEffect(() => {
    dispatch(getCourseNestedDetail(courseId));
  }, [dispatch, courseId]);

  const toggleModule = (moduleId) => {
    setOpenModules((prev) => ({
      [moduleId]: !prev[moduleId] || false,
    }));

    dispatch(getChaptersByModuleId(moduleId));
  };

  useEffect(() => {
    if (firstCourseRender.current) {
      firstCourseRender.current = false;
      return;
    }

    if (approvalStatusChangeResponse) {
      if (approvalStatusChangeResponse.error) {
        setStatusDialogMessage(
          "Failed to update approval status: " +
            approvalStatusChangeResponse.error
        );
        setStatusType("REJECTED");
      } else {
        const message =
          approvalStatusChangeResponse.message ||
          "Approval status updated successfully!";
        setStatusDialogMessage(message);
        setStatusType(approvalStatusChangeResponse.status);
      }
      setItem("Course");
      setOpenStatusDialog(true);
    }
  }, [approvalStatusChangeResponse]);

  useEffect(() => {
    if (firstModuleRender.current) {
      firstModuleRender.current = false;
      return;
    }

    if (moduleApprovalStatusChangeResponse) {
      if (moduleApprovalStatusChangeResponse.error) {
        setStatusDialogMessage(
          "Failed to update module approval status: " +
            moduleApprovalStatusChangeResponse.error
        );
        setStatusType("REJECTED");
      } else {
        const message =
          moduleApprovalStatusChangeResponse.message ||
          "Module approval status updated successfully!";
        setStatusDialogMessage(message);
        setStatusType(moduleApprovalStatusChangeResponse.status);
      }
      setItem("Module");
      setOpenStatusDialog(true);
    }
  }, [moduleApprovalStatusChangeResponse]);

  if (loading || !course) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <CircularProgress color="primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <AdminSidebar />
      <div className="flex-1 p-8">
        <Box className="bg-white rounded-2xl shadow-lg flex flex-col lg:flex-row p-6 gap-8 mb-10">
          {/* Thumbnail - Small */}
          <div className="flex-shrink-0 w-full lg:w-1/4">
            <img
              src={course.thumbnailUrl}
              alt={course.title}
              className="rounded-lg shadow w-full object-cover h-40"
            />
          </div>

          {/* Course Info */}
          <div className="flex-1 space-y-4">
            <h1 className="text-3xl font-bold text-gray-800">{course.name}</h1>

            <p className="text-gray-600 flex items-center gap-2 text-lg">
              <User className="text-indigo-500" size={20} /> Instructor:{" "}
              <span className="font-medium">{course.instructor}</span>
            </p>

            <p className="text-gray-700 text-base leading-relaxed border-l-4 border-indigo-300 pl-4">
              {course.description}
            </p>

            <p className="text-2xl font-semibold text-indigo-600 flex items-center gap-2">
              <IndianRupee size={20} /> {course.price}
            </p>

            <div className="flex gap-4">
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApprove("course", course.id)}
                disabled={
                  course.approvalStatus === "APPROVED" ||
                  clickedStatus.course === "APPROVE"
                }
              >
                Approve Course
              </Button>

              <Button
                variant="outlined"
                color="error"
                onClick={() => handleReject("course", course.id)}
                disabled={
                  course.approvalStatus === "REJECTED" ||
                  clickedStatus.course === "REJECT"
                }
              >
                Reject Course
              </Button>
            </div>
          </div>
        </Box>

        {/* Modules & Chapters */}
        <Box className="bg-white p-6 rounded-2xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Layers3 className="text-indigo-500" /> Course Modules
          </h2>
          <div className="flex flex-col gap-4">
            {course.modules.map((mod) => (
              <div key={mod.id} className="border border-indigo-100 rounded-xl">
                <Box
                  onClick={() => toggleModule(mod.id)}
                  className="w-full px-4 py-4 bg-indigo-100 hover:bg-indigo-200 text-indigo-900 font-semibold rounded-xl transition cursor-pointer shadow-sm hover:shadow-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                >
                  {/* Left: Module Title */}
                  <div className="flex items-center gap-3 text-lg">
                    <List className="text-indigo-600" />
                    <span>{mod.title}</span>
                  </div>

                  {/* Right: Approve/Reject + Expand Icon */}
                  <div
                    className="flex items-center gap-3"
                    onClick={(e) => e.stopPropagation()} // prevent toggle on button click
                  >
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleApprove("module", mod.id)}
                      disabled={
                        mod.approvalStatus === "APPROVED" ||
                        clickedStatus.module[mod.id] === "APPROVE"
                      }
                    >
                      Approve
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleReject("module", mod.id)}
                      disabled={
                        mod.approvalStatus === "REJECTED" ||
                        clickedStatus.module[mod.id] === "REJECT"
                      }
                    >
                      Reject
                    </Button>

                    <div className="text-indigo-500">
                      {openModules[mod.id] ? <ChevronUp /> : <ChevronDown />}
                    </div>
                  </div>
                </Box>

                {/* <div>{chaptersByModule?.chapters[0].title}</div> */}

                {openModules[mod.id] && (
                  <div className="bg-gray-50 px-6 py-3 space-y-2">
                    {chaptersLoading[mod.id] ? (
                      <p className="text-gray-500 italic">
                        Loading chapters...
                      </p>
                    ) : chaptersByModule && chaptersByModule.chapters ? (
                      chaptersByModule.chapters.length > 0 ? (
                        chaptersByModule.chapters.map((ch, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-gray-700 pl-2 border-l-4 border-indigo-300 font-medium pr-2"
                          >
                            <span>• {ch.title}</span>
                            <IconButton
                              color="primary"
                              onClick={() => handlePlayClick(ch.videoUrl)} // Make sure `videoUrl` exists in chapter
                            >
                              <PlayCircle className="w-5 h-5 text-indigo-500" />
                            </IconButton>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 italic">
                          No chapters available
                        </p>
                      )
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Box>
      </div>
      <Dialog
        open={openVideoDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-semibold">Chapter Video</span>
          <IconButton onClick={handleCloseDialog}>
            <X />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="relative w-full pt-[100%]">
            {" "}
            {/* 16:9 aspect ratio */}
            <iframe
              src={selectedVideoUrl}
              title="Chapter Video"
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 2,
            minWidth: 320,
            backgroundColor: "#fff",
            boxShadow: 6,
          },
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" p={2}>
          {statusType === "APPROVED" ? (
            <CheckCircleIcon sx={{ fontSize: 60, color: green[500] }} />
          ) : statusType === "REJECTED" ? (
            <CancelIcon sx={{ fontSize: 60, color: red[500] }} />
          ) : null}

          <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", mt: 1 }}>
            {statusType === "APPROVED"
              ? `${item} Approved`
              : statusType === "REJECTED"
              ? `${item} Rejected`
              : "Status Update"}
          </DialogTitle>

          <DialogContent>
            <Typography textAlign="center" sx={{ fontSize: 16 }}>
              {statusDialogMessage}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenStatusDialog(false);
              }}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              OK
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default CourseNestedDetail;
